import React, { useRef, useState, useEffect } from 'react';
import { Package, Ruler, Handshake, Key, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const BusinessModels = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeModel, setActiveModel] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    });
  };

  const models = [
    { title: t('business.model1.title'), description: t('business.model1.description'), Icon: Package },
    { title: t('business.model2.title'), description: t('business.model2.description'), Icon: Ruler },
    { title: t('business.model3.title'), description: t('business.model3.description'), Icon: Handshake },
    { title: t('business.model4.title'), description: t('business.model4.description'), Icon: Key }
  ];

  const ActiveIcon = models[activeModel].Icon;

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden" onMouseMove={handleMouseMove}>
      <div 
        className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl transition-all duration-1000 ease-out pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(220, 38, 38, 0.3) 0%, transparent 70%)',
          left: `${mousePosition.x * 100}%`,
          top: `${mousePosition.y * 100}%`,
          transform: 'translate(-50%, -50%)'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="text-red-600 animate-pulse" size={20} />
              <span className="text-red-600 text-sm font-semibold uppercase tracking-wider">{t('business.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              {t('business.title')}
              <span className="block text-neutral-600 text-3xl mt-2">{t('business.subtitle')}</span>
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed mb-8">{t('business.description')}</p>

            <div className="space-y-4">
              {models.map((model, index) => {
                const Icon = model.Icon;
                return (
                  <div key={index} onClick={() => setActiveModel(index)}
                    className={`relative p-5 rounded-xl cursor-pointer transition-all duration-500 transform ${
                      activeModel === index 
                        ? 'bg-neutral-900 text-white shadow-xl scale-[1.02]' 
                        : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100 hover:scale-[1.01]'
                    } ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${index * 100}ms` }}>
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-500 ${
                        activeModel === index ? 'bg-red-600 rotate-6' : 'bg-neutral-200 rotate-0'
                      }`}>
                        <Icon size={24} className={`transition-all duration-500 ${activeModel === index ? 'text-white scale-110' : 'text-neutral-700'}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-lg mb-1">{model.title}</h4>
                        <p className={`text-sm ${activeModel === index ? 'text-neutral-300' : 'text-neutral-600'}`}>{model.description}</p>
                      </div>
                    </div>
                    {activeModel === index && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-red-600 rounded-r-full animate-pulse" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div 
              className="relative h-[500px] transition-transform duration-500"
              style={{
                transform: `perspective(1000px) rotateY(${(mousePosition.x - 0.5) * 10}deg) rotateX(${(mousePosition.y - 0.5) * -10}deg)`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-3xl shadow-2xl p-8 flex flex-col justify-between overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute w-full h-full" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '24px 24px'
                  }} />
                </div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-red-600 flex items-center justify-center mb-6 animate-pulse">
                    <ActiveIcon size={32} className="text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4">{models[activeModel].title}</h3>
                  <p className="text-neutral-400 text-lg">{models[activeModel].description}</p>
                </div>
                <div className="relative z-10 grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm hover:bg-white/20 transition-colors">
                    <p className="text-neutral-400 text-sm">{t('business.investment')}</p>
                    <p className="text-white text-xl font-bold">{t('business.flexible')}</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm hover:bg-white/20 transition-colors">
                    <p className="text-neutral-400 text-sm">{t('business.returns')}</p>
                    <p className="text-white text-xl font-bold">{t('business.optimised')}</p>
                  </div>
                </div>
                
                <div className="absolute top-10 right-10 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                <div className="absolute bottom-20 right-20 w-1 h-1 bg-white rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessModels;
